import axios from 'lc-http';
import React, { Component } from 'react';
import { shape } from 'prop-types';
import objectPath from 'object-path';
import { getStreetAddress } from '@shared_modules/baui-utils';
import TealiumTrack from '@shared_modules/baui-utils/client/utils/tealium-track';
import { trackHeapEvent } from '@shared_modules/custom-heap-tracking-attributes/custom-heap-tracking-attributes';
import { heapTrackErrors } from '@shared_modules/baui-heap-tracking';
import { ProgressBar, ResponsiveContainer } from './components';
import Routing from './Routing';
import BeforeUnload from './hooks/BeforeUnload';
import commonErrors from '@shared_modules/common-properties/errors';
import context from './serverContext';
// TODO: consolidate all utils into one module
import computeSteps from './utils/flow-definition';
import computeInitialFields from './utils/initial-fields';
import sendExtoleRegistrationEvent from '@shared_modules/extole-tracking/extole-registration';
import { captureEmail, checkEmail } from './utils/email-utils';
import { appendQueryStrToNextPage, getBtMktLoanPurposeTrackingAttrs, removeFromArray } from './utils/util';
import { LCContext } from '@shared_modules/local-propulsion/components';
import { STEPS } from './utils/constants';

const fieldsModel = require('./utils/fieldsModel.json');

const {
  previousSecondaryBorrower, // jointBorrowerForPrefill
  isRMEJA,
  experiments,
  isLoggedIn,
  environment,
  hasExtoleReferrer,
  heapTrackingPage,
  dm,
} = context;

class PI1 extends Component {
  static contextType = LCContext;
  captureEmail = captureEmail.bind(this);
  checkEmail = checkEmail.bind(this);

  state = {
    fields: computeInitialFields(context),
    ssnRequired: { primary: false, secondary: false },
    showSpinner: false,
    showUnloadPrompt: false,
    showIncomeAdditional: false,
    showEditAddress: { primary: false, secondary: false },
    addNewCoBorrower: false,
    isAuthenticationRequired: false,
    parsedEmailPasswordErrors: {},
    submitAttempt: 0,
    isLoggedIn: isLoggedIn || false, // TODO reset back to just false...
    isLoginWidgetSuccess: false,
    checkEmailErrors: [],
    isRepeatsUser: false,
    showSentilinkSSNConsent: false,
  };
  // TODO: reactjs unsafe lifecycle method, will need to update this
  UNSAFE_componentWillMount = () => {
    // PI1 steps initialization
    this.updateSteps();
  };

  componentDidMount = () => {
    // do not allow browser to scroll back to cached scroll position
    if ('scrollRestoration' in history) {
      try {
        history.scrollRestoration = 'manual';
      } catch (e) {
        /* do nothing */
      }
    }
  };

  // TODO: move logic to the place loanType updated
  UNSAFE_componentWillReceiveProps = () => {
    // update steps after loanType (props.location changed when loanType->name step)
    this.updateSteps();
    // Clear server errors when navigating between pages
    this.setState({ parsedEmailPasswordErrors: {} });
  };

  onLoginWidgetSuccess = async () => {
    const isDmOrIsLoggedIn = dm.isDm || this.state.isLoggedIn;
    if (!isDmOrIsLoggedIn) {
      try {
        const user = await axios({
          method: 'POST',
          url: '/apply/personal/identity/validate-user',
        });

        const { isRepeatsUser } = user.data;

        if (isRepeatsUser) {
          this.setState({ showUnloadPrompt: false, isLoggedIn: true, isLoginWidgetSuccess: true, isRepeatsUser });
          const qsName = 'RM_From_1QAAT'; // Repeat Member From 1QAAT to pointing a repeats user going to repeats flow
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete(qsName);
          const reOrderQs = `?${searchParams.size ? searchParams.toString() + '&' : ''}${qsName}=1`;
          return location.assign(`/apply/personal/interstitial${reOrderQs}`);
        }
      } catch (error) {
        // In case of any error the user will follow the default process
      }
    }

    return this.setState({ isLoginWidgetSuccess: true, isLoggedIn: !isDmOrIsLoggedIn });
  };

  onIncomeAdditionalClick = (value, cb) => {
    this.updateSteps(value);
    this.setState(value, cb);
  };

  getCurrentStep = () => {
    let currentFormIndex = this.steps.allSteps.indexOf(this.props.location.pathname);
    if (currentFormIndex === -1) currentFormIndex = 0;
    return currentFormIndex;
  };

  // saveValues() on routes /secondary/* does not support saving emailSecondary
  saveCoApplicantEmail = email => {
    const fields = this.state.fields;
    Object.assign(fields, { emailSecondary: email });
    return this.setState({ fields });
  };

  setFormProps = () => {
    const pathName = this.props.location.pathname;
    const steps = this.steps;
    const isDM = dm.isDm;
    const isDmOrRME = isDM || context.isFromSecondLoanFlow;

    const formProps = {
      saveValues: this.saveValues,
      nextLocation: this.goToNextLocation,
      previousLocation: this.goToPreviousLocation,
      onIncomeAdditionalClick: this.onIncomeAdditionalClick,
      showIncomeAdditional: this.state.showIncomeAdditional,
      showEditAddress: this.state.showEditAddress,
      addNewCoBorrower: this.state.addNewCoBorrower,
      isSecondary: steps.secondarySteps.indexOf(pathName) > -1,
      isJoint: this.state.fields.loan.loanType === 'joint',
      captureEmail: this.captureEmail,
      checkEmail: this.checkEmail,
      isLoggedIn: this.state.isLoggedIn,
      onLoginWidgetSuccess: this.onLoginWidgetSuccess,
      isRMEJA,
      parsedEmailPasswordErrors: this.state.parsedEmailPasswordErrors,
      saveCoApplicantEmail: this.saveCoApplicantEmail,
      experiments,
      borrowerHistory: this.state.fields.borrowerHistory,
      updateUnloadPrompt: this.updateUnloadPrompt,
      isRepeatsUser: this.state.isRepeatsUser,
      isFromSecondLoanFlow: context.isFromSecondLoanFlow,
      isDM,
    };
    // pathName matching last step marked in allSteps array
    const currentStepIndex = steps.allSteps.indexOf(pathName);
    const isLastPage = currentStepIndex === steps.allSteps.length - 1;

    if (isLastPage || (!isDmOrRME && pathName === STEPS.PASSWORD)) {
      Object.assign(formProps, {
        submitAttempt: this.state.submitAttempt,
        isAuthenticationRequired: this.state.isAuthenticationRequired,
        nextLocation: isLastPage ? this.submitData : this.goToNextLocation,
        checkEmailErrors: this.state.checkEmailErrors,
      });
    } else if (!isDmOrRME && pathName === STEPS.CONTINUE_APP) {
      Object.assign(formProps, {
        checkEmailErrors: this.state.checkEmailErrors,
      });
    } else if (
      currentStepIndex === steps.allSteps.length - 2 ||
      (isDM && currentStepIndex === steps.allSteps.length - 1)
    ) {
      Object.assign(formProps, {
        submitAttempt: this.state.submitAttempt,
        isAuthenticationRequired: this.state.isAuthenticationRequired,
      });
    } else if (pathName.includes('secondary')) {
      // lastStep does not save to primary OR secondary
      Object.assign(formProps, {
        saveValues: (values, callback) => {
          const type = !isDmOrRME && pathName.includes('secondary-email') ? '' : 'secondary';
          this.saveValues(values, type, callback);
        },
      });
    } else if (pathName.includes('primary')) {
      Object.assign(formProps, { saveValues: (values, callback) => this.saveValues(values, 'primary', callback) });
    }

    return formProps;
  };

  steps = {};

  updateSteps = options => {
    if (options && options.removeContinueAppStep) this.steps.allSteps.pop();
    else {
      const computeStepsProps = {
        context,
        loanType: this.state.fields.loan.loanType,
        ssnRequired: this.state.ssnRequired,
        showIncomeAdditional: options ? options.showIncomeAdditional : this.state.showIncomeAdditional,
        showEditAddress: options ? options.showEditAddress : this.state.showEditAddress,
        addNewCoBorrower: options ? options.addNewCoBorrower : this.state.addNewCoBorrower,
        isLoggedIn: this.state.isLoggedIn,
        errors: this.state.errors,
        steps: this.steps,
        checkEmailErrors: this.state.checkEmailErrors,
      };
      this.steps = computeSteps(computeStepsProps);
    }
  };

  isAccountCreated = () => {
    const account = this.state.fields.account;
    return account.email !== '' && account.password !== '' && account.agreements === 'on';
  };

  goToNextLocation = () => {
    // Remove continue-app step from flow once borrower goes back from errorPage. We will show it again if PBS returns errors again.
    if (this.steps.allSteps.includes('/continue-app')) this.updateSteps({ removeContinueAppStep: true });
    const currentFormIndex = this.getCurrentStep();
    const allSteps = this.steps.allSteps;

    if (currentFormIndex < allSteps.length - 1) {
      this.props.history.push({
        pathname: allSteps[currentFormIndex + 1],
        search: this.props.location.search,
      });
    }
  };

  goToPreviousLocation = () => {
    window.history.back();
  };

  errorHandler = data => {
    const errors = data.err;
    const firstError = objectPath.get(data, 'err.0.error'); // ssn error should be here
    const newState = {};
    // don't show the dialog box (stay/leave options) if borrower is logged in after error is returned (does this apply to loan app in progress case or below? TODO investigate)
    if (data.partyId) {
      // if (data.actorId) { // originally (loanId also seems to be missing here when we get this err. why? pbs respone has it, fs response should). need further investigation TODO
      Object.assign(newState, { showUnloadPrompt: false, isLoggedIn: true });
    }

    if (firstError.indexOf('PI1_SSN') > -1) {
      // if Unhappy SSN path occurs
      const withoutSSNErrors = errors.filter(
        err => !/PRIMARY/.test(firstError) && !/SECONDARY/.test(firstError) && err !== 'IDENTITY_NOT_VERIFIED'
      );
      Object.assign(newState, {
        showSpinner: false,
        ssnRequired: { primary: /PRIMARY/.test(firstError), secondary: /SECONDARY/.test(firstError) },
        withoutSSNErrors,
        showSentilinkSSNConsent: data?.showSentilinkSSNConsent,
      });
      this.registerWithExtole(data);
      this.setState(newState);
      // update steps after get ssn unhappy
      this.updateSteps();
      this.props.history.push({
        pathname: '/ssn',
        search: this.props.location.search,
      });
    } else {
      // other error cases
      Object.assign(newState, { errors, showSpinner: false });
      this.setState(newState); // hide spinner on error
      this.updateSteps();
      if (this.props.location.pathname !== '/continue-app') {
        this.props.history.push({
          pathname: '/continue-app',
          search: this.props.location.search,
        });
      }
    }
  };

  submitHandler = ({ data }) => {
    trackHeapEvent('PL_PI1_Submit_Response', undefined, {
      loanId: data.loanId,
      actorId: data.actorId,
      err: data.err,
      state: data.state,
      nextPage: data.nextPage,
      ...(data?.heapTrackingAttrs || {}),
      ...getBtMktLoanPurposeTrackingAttrs({
        appendAttrs: { LOAN_PURPOSE: data?.loanPurpose },
      }),
    });
    if (data.err) {
      heapTrackErrors(data.err);
      this.errorHandler(data);
    } else {
      this.registerWithExtole(data);
      if (window.nid) {
        window.nid('setCheckpoint', 'appCreated');
      }
      TealiumTrack.link({
        PI1_Submission: 'true',
        loan_amount: this.state.fields.prequal.loanAmount,
        loan_purpose: this.state.fields.prequal.loanPurpose,
      });
      // Borrower submitted PI1 successfully.
      // don't show the dialog box (stay/leave options) when page is reloaded with next page.
      this.setState({ showUnloadPrompt: false, nextPage: data.nextPage, isLoggedIn: !!data.actorId });
      const nextPage = appendQueryStrToNextPage(data);
      window.location.assign(nextPage);
    }
  };

  updateUnloadPrompt = value => this.setState({ showUnloadPrompt: value });

  registerWithExtole = data => {
    if (hasExtoleReferrer) {
      const extoleRegistrationData = {
        nodeEnv: environment.nodeEnv,
        primaryActorId: data.partyId || data.actorId, // partyId is undefined for the very first loan app
        actorId: data.actorId,
        loanId: data.loanId,
        email: this.state.fields.email,
        firstName: this.state.fields.primary.name.firstName,
        lastName: this.state.fields.primary.name.lastName,
      };
      sendExtoleRegistrationEvent(extoleRegistrationData);
    }
  };

  submitData = () => {
    const formData = this.flattenFormData();
    this.setState({ errors: [], showSpinner: true, submitAttempt: this.state.submitAttempt + 1 });
    axios({
      method: 'POST',
      url: '/apply/personal/identity/submit',
      data: formData,
    })
      .then(this.submitHandler)
      .catch(error => {
        heapTrackErrors(error);
        this.errorHandler({
          err: [
            {
              error: error.response?.status !== 428 ? 'UNEXPECTED_ERROR' : 'CAPTCHA_ERROR_HANDLER',
              message: commonErrors.UNEXPECTED_ERROR,
            },
          ],
        });
      });
  };

  flattenFormData = () => {
    let formData = {};
    formData = {
      ...this.state.fields.loan,
      ...this.state.fields.dmCode, // for DM funding code
      ...this.state.fields.ssn,
      email: this.state.fields.email,
      password: this.state.fields.password,
      agreements: this.state.fields.agreements,
      isMFAReturn: this.state.isLoginWidgetSuccess,
      prescreenPayLoad: this.context.prescreenPayLoad,
      originator: this.context.originator.value, // for signing ApplicationTermsOfUse agreement based on originator, passed from server to client on load (preserve what the user initially saw on load, even if bank is switched by the time they submit)
      experiments: this.state.fields.experiments,
    };

    if (this.state.fields.loan.loanType === 'joint') {
      formData.emailSecondary = this.state.fields.emailSecondary;
    }

    Object.assign(formData, this.state.fields.prequal);

    const types = this.state.fields.loan.loanType === 'joint' ? ['primary', 'secondary'] : ['primary'];

    types.forEach(type => {
      Object.keys(this.state.fields[type]).forEach(group =>
        Object.keys(this.state.fields[type][group]).forEach(field =>
          Object.assign(formData, {
            [type === 'primary' ? field : `${field}Secondary`]: this.state.fields[type][group][field],
          })
        )
      );
    });

    if (this.state.ssnRequired.primary) {
      formData.primarySSNRequired = true;
      formData.ssn = this.state.fields.ssn;
      formData.sentilinkConsent = this.state.fields.sentilinkConsent;
    }

    if (this.state.ssnRequired.secondary) {
      formData.secondarySSNRequired = true;
      formData.ssnSecondary = this.state.fields.ssnSecondary;
    }

    // Possible for borrower to select the checkbox, go to AdditionalIncome screen provide the value
    // Click back button, uncheck the checkbox on Income screen. In that case, we want to unset the
    // IncomeAdditional value that was collected before. Don't add to formData which is sent to service.
    if (!this.state.showIncomeAdditional) {
      delete formData.incomeAdditional;
    }

    if (context.isFromSecondLoanFlow) {
      delete formData.firstName;
      delete formData.lastName;
      delete formData.monthBirth;
      delete formData.dayBirth;
      delete formData.yearBirth;
    }

    if (this.state.isLoginWidgetSuccess) {
      delete formData.password;
    }

    // This should be the last condition as we re-construct the formData for LockedPI1 test.
    if (this.state.isLoggedIn) {
      delete formData.email;
      delete formData.password;
    }

    return formData;
  };

  saveValues = (values, type, callback) => {
    const fields = this.state.fields;
    if (type) {
      Object.assign(fields[type], values);
    } else {
      Object.assign(fields, values);
    }
    const { newFields, addNewCoBorrower } = this.checkUseNewSecondaryBorrower(fields);
    if (callback && typeof callback === 'function') {
      // For RME, when co-borrower name update, may result to a steps update
      return this.setState({ fields: newFields, addNewCoBorrower, showUnloadPrompt: true }, () => {
        this.updateSteps();
        return callback();
      });
    }
    this.updateSteps();
    return this.setState({ fields: newFields, addNewCoBorrower, showUnloadPrompt: true });
  };

  clearSecondaryBorrower = fields => {
    const initialFields = JSON.parse(JSON.stringify(fieldsModel)); // make clone and avoid mutate fieldsModel by accident
    return Object.assign(fields, { secondary: initialFields.secondary });
  };
  prefillSecondaryBorrower = fields => {
    const initialFields = JSON.parse(JSON.stringify(fieldsModel)); // make clone and avoid mutate fieldsModel by accident
    return Object.assign(fields, {
      secondary: {
        ...initialFields.secondary,
        dob: {
          monthBirth: objectPath.get(previousSecondaryBorrower, 'monthBirth', ''),
          dayBirth: objectPath.get(previousSecondaryBorrower, 'dayBirth', ''),
          yearBirth: objectPath.get(previousSecondaryBorrower, 'yearBirth', ''),
        },
        name: {
          firstName: objectPath.get(previousSecondaryBorrower, 'firstName', ''),
          lastName: objectPath.get(previousSecondaryBorrower, 'lastName', ''),
        },
        address: {
          streetAddress: getStreetAddress(objectPath.get(previousSecondaryBorrower, 'address')),
          city: objectPath.get(previousSecondaryBorrower, 'address.city', ''),
          state: objectPath.get(previousSecondaryBorrower, 'address.state', ''),
          zip: objectPath.get(previousSecondaryBorrower, 'address.zip', ''),
          secondaryUsePrimaryAddress: false,
        },
      },
    });
  };

  // pass thru, could be more efficient. returns fields usually
  checkUseNewSecondaryBorrower = fields => {
    const newFields = JSON.parse(JSON.stringify(fields));
    let addNewCoBorrower = this.state.addNewCoBorrower;
    const inputedSecondaryName = newFields.secondary.name;
    if (isRMEJA && this.props.location.pathname === '/secondary-name') {
      // this logic should probably go on the page itself.
      if (
        inputedSecondaryName.firstName === previousSecondaryBorrower.firstName &&
        inputedSecondaryName.lastName === previousSecondaryBorrower.lastName
      ) {
        // if the secondary borrower's name typed matches from a previous LOAN, (re)prefill the rest
        this.prefillSecondaryBorrower(newFields);
        addNewCoBorrower = false;
      } else {
        // basically lots of fanangling to clear DOB etc if the name changes
        this.clearSecondaryBorrower(newFields);
        newFields.secondary.name = inputedSecondaryName;
        addNewCoBorrower = true;
      }
    }
    return { newFields, addNewCoBorrower };
  };

  renderProgressBar = () => {
    // TODO: below seems WET with formProps
    const showProgressBarOnHistoryPage = this.props.location.pathname === STEPS.BORROWER_HISTORY;
    const showProgressBarOnAppTypePage = this.props.location.pathname === STEPS.APP_TYPE;
    const stepsWithoutProgressBar = ['/', STEPS.APP_TYPE, STEPS.LOAN_TYPE, STEPS.CONTINUE_APP, STEPS.BORROWER_HISTORY];

    if (!dm.isDm) {
      removeFromArray(stepsWithoutProgressBar, STEPS.LOAN_TYPE);
      if (this.state.isAuthenticationRequired) stepsWithoutProgressBar.push(STEPS.PASSWORD);
    }

    const progressBarProps = {
      showProgressBar:
        !stepsWithoutProgressBar.includes(this.props.location.pathname) ||
        showProgressBarOnHistoryPage ||
        showProgressBarOnAppTypePage,
      pathName: this.props.location.pathname,
      steps: {
        allSteps: this.steps.allSteps,
        primarySteps: this.steps.primarySteps,
        secondarySteps: this.steps.secondarySteps,
      },
      primaryFirstName: this.state.fields.primary.name.firstName,
      secondaryFirstName: this.state.fields.secondary.name.firstName,
      isJoint: this.state.fields.loan.loanType === 'joint',
    };

    return <ProgressBar {...progressBarProps} />;
  };

  render() {
    const formProps = this.setFormProps();
    const searchParams = new URLSearchParams(window.location.search);
    const webview = searchParams.get('webview');

    if (webview === 'true') {
      window.localStorage.setItem('webview', 'true');
    }

    const routingProps = {
      formProps,
      allSteps: this.steps.allSteps,
      location: this.props.location,
      history: this.props.history,
      fields: this.state.fields,
      ssnRequired: this.state.ssnRequired,
      showUnloadPrompt: this.state.showUnloadPrompt,
      nextPage: this.state.nextPage,
      error: this.state.errors,
      loanType: this.state.fields.loan.loanType,
      isRepeatsUser: this.state.isRepeatsUser,
      showSentilinkSSNConsent: this.state.showSentilinkSSNConsent,
    };

    return (
      <ResponsiveContainer
        tracking={{
          loanType: this.state.fields.loan.loanType,
          currentStep: this.steps.allSteps[this.getCurrentStep()],
          heapTrackingPage,
        }}
        pathname={this.props.location.pathname}
        showSpinner={this.state.showSpinner}
        errors={this.state.errors}
        experiments={experiments}
      >
        <BeforeUnload showPrompt={this.state.showUnloadPrompt} />
        {this.renderProgressBar()}
        <Routing {...routingProps} />
      </ResponsiveContainer>
    );
  }
}

PI1.propTypes = {
  location: shape({}).isRequired,
  history: shape({}).isRequired,
};
export default PI1;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/pi1-1QAAT.scss');